import { create } from "zustand";

const DAY_MIN = 24 * 60;
const SNAP_MIN = 15;

function snap(min: number): number {
  return Math.round(min / SNAP_MIN) * SNAP_MIN;
}

function clampTimes(startMin: number, durationMin: number): { startMin: number; durationMin: number } {
  const nextStart = Math.max(0, Math.min(DAY_MIN - SNAP_MIN, snap(startMin)));
  const maxDuration = DAY_MIN - nextStart;
  return {
    startMin: nextStart,
    durationMin: Math.max(SNAP_MIN, Math.min(maxDuration, snap(durationMin))),
  };
}

/**
 * Bridge between the mobile create/edit sheet and the on-grid draggable ghost.
 *
 * The sheet's schedule lives in `useTaskForm`'s local state (a hook instance),
 * while the ghost renders in the calendar tree — a different component subtree.
 * This store carries the live draft so both can read it: the sheet `publish()`es
 * its form schedule (form → ghost) and the ghost's drag/resize calls
 * `setTimes`/`setDay` (ghost → sheet). The sheet's sync effects use value
 * equality so the two directions converge instead of ping-ponging.
 *
 * Replaces the old quick-create store: the throwaway quick-create sheet is gone;
 * only its on-grid ghost survives, now driven by this bridge.
 */
interface MobileSheetDraftState {
  /** True while a timed create/edit draft should paint a ghost on the grid. */
  active: boolean;
  /** Day the pending entry sits on (midnight, local). */
  day: Date | null;
  /** Start, minutes from midnight. */
  startMin: number;
  durationMin: number;
  title: string;
  isEvent: boolean;
  /** The edited task's id (edit mode) so its real block can dim under the
   *  ghost; null while creating. */
  sourceId: string | null;
  /**
   * Bumped by ghost-originated moves ONLY (`setTimes`/`setDay`), so the sheet
   * can tell a real drag from the echo of its own `publish`. Without it the
   * bounding `publish` does below (a cross-midnight or zero-length entry) came
   * straight back into the form as an edit the user never made — which both
   * truncated the task on save and made an untouched sheet ask to discard.
   */
  gesture: number;
  /** Sheet → ghost: publish the form's current schedule (does not snap). */
  publish: (d: { day: Date; startMin: number; durationMin: number; title: string; isEvent: boolean; sourceId: string | null }) => void;
  /** Ghost → sheet: move/resize (snapped + clamped here). */
  setTimes: (startMin: number, durationMin: number) => void;
  /** Ghost → sheet: drag across days. */
  setDay: (day: Date) => void;
  /** Tear down the ghost (sheet closed / went all-day / undated). */
  clear: () => void;
}

export const useMobileSheetDraftStore = create<MobileSheetDraftState>((set) => ({
  active: false,
  day: null,
  startMin: 9 * 60,
  durationMin: 60,
  title: "",
  isEvent: true,
  sourceId: null,
  gesture: 0,
  publish: ({ day, startMin, durationMin, title, isEvent, sourceId }) => {
    // Bound to the day WITHOUT snapping: the sheet's sync effect writes store
    // values back into the form, so snapping here would silently rewrite a
    // deliberate off-grid time or sub-15-minute duration the user picked.
    const s = Math.max(0, Math.min(DAY_MIN - 1, startMin));
    const d = Math.max(1, Math.min(DAY_MIN - s, durationMin));
    set({ active: true, day, startMin: s, durationMin: d, title, isEvent, sourceId });
  },
  setTimes: (startMin, durationMin) => set((s) => ({ ...clampTimes(startMin, durationMin), gesture: s.gesture + 1 })),
  setDay: (day) => set((s) => ({ day, gesture: s.gesture + 1 })),
  clear: () => set({ active: false, day: null, sourceId: null }),
}));
